import type { ApiResponse, FieldErrors, LoginResponseData } from '@farm/shared';

/**
 * API client.
 *
 * Two things it exists to guarantee:
 *
 *   1. **The access token never leaves memory.** No localStorage, no
 *      sessionStorage — an XSS bug should not yield a token an attacker can
 *      keep. The refresh token is in an HttpOnly cookie the SPA cannot read at
 *      all, so a page reload re-authenticates through `/auth/refresh` rather
 *      than reading a token from disk.
 *
 *   2. **A 401 is retried exactly once, after a single shared refresh.** With
 *      a 15-minute access token, several requests will expire together — the
 *      tree page alone fires the header, the stats and the timeline. Without
 *      the shared in-flight promise below, that is three concurrent refreshes,
 *      and since refresh tokens rotate, two of them would present an
 *      already-used token and trip reuse detection. The user would be silently
 *      logged out for doing nothing wrong.
 */

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly errors?: FieldErrors,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the failure is a per-field validation problem a form can show. */
  get isValidation(): boolean {
    return this.status === 422 && this.errors !== undefined;
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when refresh fails — the app should route to login. */
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Internal: prevents a refresh loop on the refresh call itself. */
  skipRefresh?: boolean;
  signal?: AbortSignal;
  /**
   * Idempotency key for POSTs that create records. Field connections drop
   * mid-request and a double-tap must not create two work orders (§10).
   */
  idempotencyKey?: string;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(opts.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  const response = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    // Sends the HttpOnly refresh cookie. Same-origin, so no CORS involved.
    credentials: 'same-origin',
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  if (response.status === 401 && !opts.skipRefresh) {
    const refreshed = await refreshOnce();
    if (refreshed) return request<T>(path, { ...opts, skipRefresh: true });
    onSessionLost?.();
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as ApiResponse<T> | null;

  if (!response.ok || payload === null) {
    const message = payload?.message ?? 'Something went wrong';
    const code = payload && 'code' in payload ? payload.code : undefined;
    const errors = payload && 'errors' in payload ? payload.errors : undefined;
    throw new ApiError(message, response.status, code, errors);
  }

  return (payload as { data: T }).data;
}

/**
 * Refresh at most once concurrently.
 *
 * Every caller that arrives while a refresh is in flight awaits the same
 * promise. This is the guard against rotation-induced reuse detection described
 * at the top of the file.
 */
function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const data = await request<LoginResponseData>('/auth/refresh', {
        method: 'POST',
        body: {},
        skipRefresh: true,
      });
      setAccessToken(data.token);
      return true;
    } catch {
      setAccessToken(null);
      return false;
    } finally {
      // Cleared in a microtask so callers that queued during this refresh have
      // already attached to the promise before it disappears.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/**
 * Upload raw bytes to wherever `/uploads/presign` said to put them.
 *
 * Deliberately outside `request()`: the target may be R2 directly (a presigned
 * S3 URL on another origin) rather than this API, and sending our access token
 * to a third-party host would be handing out a credential. The presigned URL
 * carries its own authorisation in the query string; the proxy fallback is
 * same-origin and gets the bearer token.
 */
async function putRaw(
  url: string,
  body: Blob,
  headers: Record<string, string> = {},
): Promise<void> {
  const sameOrigin = url.startsWith('/') || url.startsWith(location.origin);
  const token = getAccessToken();

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      ...headers,
      ...(sameOrigin && token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
  });

  if (!response.ok) {
    throw new ApiError(`Upload failed (${response.status})`, response.status);
  }
}

/**
 * Fetch an authenticated binary (an image, a PDF) as a Blob.
 *
 * A plain `<img src>` cannot do this: the browser issues that request without
 * the Authorization header, and the token is in memory rather than a cookie by
 * design (§8). Retries once through the shared refresh, exactly as `request()`
 * does, so an expired token does not turn every photo on a page into a broken
 * image icon.
 */
export async function authedBlob(path: string): Promise<Blob> {
  const send = async (): Promise<Response> =>
    fetch(path, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'same-origin',
    });

  let response = await send();
  if (response.status === 401) {
    const refreshed = await refreshOnce();
    if (refreshed) response = await send();
  }
  if (!response.ok) {
    throw new ApiError(`Could not load ${path}`, response.status);
  }
  return response.blob();
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, query ? { query } : {}),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>(path, { method: 'POST', body, ...(idempotencyKey ? { idempotencyKey } : {}) }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  putRaw,
};
